import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// MUI Components
import {
  Box,
  List,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Badge,
  ListItemButton,
  TextField,
  Paper,
  styled,
  Stack,
  InputAdornment,
  IconButton,
  Skeleton,
  Tooltip,
  Chip,
  Fade,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Lucide Icons
import {
  Search,
  Check,
  CheckCheck,
  AlertCircle,
  User,
  Download,
  ArrowLeft,
  X,
  MessageCircle,
  RefreshCw,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";

// Hooks & Context
import useApiEp from "../../hooks/useApiEp";
import { useAccount } from "../../context/AccountContext";

// Utils & Constants
import { API_ENDPOINT } from "../../utils/constants";
import {
  getHeader,
  toastError,
  exportToExcel,
  getToken,
} from "../../utils/util";

// Widgets & Components
import SendMessage from "../../widgets/SendMessage";
import DateRangePicker from "../../widgets/DateRangePicker";
import TemplateMessage from "./TemplateMessage";
import LookupProfile from "./LookupProfile";

// --- Design tokens ---
const AVATAR_PALETTE = [
  "#5B6CF9",
  "#E8598B",
  "#2FA88A",
  "#E2932C",
  "#8B5CF6",
  "#3B9CE8",
  "#EF6461",
  "#19A7A0",
];

const getAvatarColor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

// --- Styled Components ---
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 340,
  display: "flex",
  flexDirection: "column",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: "width 0.25s ease",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    borderRight: "none",
  },
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: "#F4F6F8",
  backgroundImage:
    "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.025) 1px, transparent 0)",
  backgroundSize: "22px 22px",
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  zIndex: 10,
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[300],
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: theme.palette.grey[400],
  },
}));

const DateDivider = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  margin: theme.spacing(1.5, 0),
}));

const MessageBubble = styled(Paper)(({ theme, isSent }) => ({
  padding: theme.spacing(1.25, 1.75),
  maxWidth: "72%",
  alignSelf: isSent ? "flex-end" : "flex-start",
  backgroundColor: isSent ? "#DCEBFF" : theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: isSent ? "14px 14px 3px 14px" : "14px 14px 14px 3px",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  border: `1px solid ${isSent ? "#C7DEFB" : theme.palette.divider}`,
  animation: "message-in 0.18s ease-out",
  "@keyframes message-in": {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const UnreadBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "50%",
  minWidth: 20,
  height: 20,
  padding: "0 6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.7rem",
  fontWeight: 700,
  border: `2px solid ${theme.palette.background.paper}`,
}));

// --- Cached Helpers (Performance Optimization) ---
const istKeyFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
});
const istLabelFormatter = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "numeric",
  month: "short",
  year: "numeric",
});
const shortMonthFormatter = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "numeric",
  month: "short",
});

const formatIST = (iso) =>
  new Date(iso)
    .toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(/\//g, "-")
    .replace(", ", " ");

const getISTDateKey = (iso) => istKeyFormatter.format(new Date(iso));

const getDateLabel = (iso) => {
  const key = getISTDateKey(iso);
  const todayKey = getISTDateKey(new Date().toISOString());
  const yesterdayKey = getISTDateKey(
    new Date(Date.now() - 86400000).toISOString()
  );
  if (key === todayKey) return "Today";
  if (key === yesterdayKey) return "Yesterday";
  return istLabelFormatter.format(new Date(iso));
};

const getRelativeTime = (iso) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return shortMonthFormatter.format(new Date(iso));
};

const getMessagePreview = (type, body) => {
  if (type === "text" || type === "button" || type === "interactive") {
    return body || "";
  }
  if (type === "template") return "Template message";
  if (!type) return body || "";
  return type.charAt(0).toUpperCase() + type.slice(1);
};

// --- Main Component ---

const Chats = () => {
  const { selectedAccount } = useAccount();
  const [searchParams] = useSearchParams();
  const paramValue = searchParams.get("tn");

  const [reloadChat, setReloadChat] = useState(false);
  const [tagType, setTagType] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchFrom, setSearchFrom] = useState("");
  const [exportingChat, setExportingChat] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [clearedUnreadFor, setClearedUnreadFor] = useState(() => new Set());
  const [messages, setMessages] = useState([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const messagesEndRef = useRef(null);

  const debouncedSetSearchFrom = useMemo(
    () => debounce((value) => setSearchFrom(value.trim()), 300),
    []
  );

  useEffect(() => {
    debouncedSetSearchFrom(searchInput);
    return () => debouncedSetSearchFrom.cancel();
  }, [searchInput, debouncedSetSearchFrom]);

  const {
    data: contacts = [],
    isLoading: isContactsLoading,
    isError: isContactsError,
    error: contactsError,
  } = useApiEp(
    `/api/get-my-chats?from=${searchFrom}&tn=${
      paramValue ? encodeURIComponent(paramValue) : ""
    }&cid=${selectedAccount}`
  );

  useEffect(() => {
    setClearedUnreadFor((prev) => {
      if (prev.size === 0) return prev;
      const stillNeeded = new Set(
        [...prev].filter((from) => {
          const match = contacts.find((c) => c.from === from);
          return match ? match.unreadCount > 0 : true;
        })
      );
      return stillNeeded.size === prev.size ? prev : stillNeeded;
    });
  }, [contacts]);

  const {
    data: chatData,
    isLoading: isChatLoading,
    isError: isChatError,
    error: chatError,
  } = useApiEp(
    `/api/user-chats/${selectedContact?.from}?reload=${reloadChat}&cid=${selectedAccount}`,
    !!selectedContact
  );

  const handleContactClick = useCallback((contact) => {
    setSelectedContact(contact);
    setClearedUnreadFor((prev) => {
      const next = new Set(prev);
      next.add(contact.from);
      return next;
    });
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedContact(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchInput("");
  }, []);

  const isMessageSent = (message) => {
    return message.from !== selectedContact?.from;
  };

  const getMessageContent = (message) => {
    if (message.type === "template") {
      return <TemplateMessage id={message._id} to={message.to} />;
    } else if (message.type === "text" || message.type === "button") {
      return message.body || message?.text;
    } else if (message.type === "interactive") {
      return message?.text || message?.body;
    }
    return message?.text || message?.body || "...";
  };

  const getStatusIcon = (status) => {
    const iconProps = { size: 14, strokeWidth: 2.5 };
    switch (status) {
      case "sent":
        return <Check {...iconProps} color="#919191" />;
      case "delivered":
        return <CheckCheck {...iconProps} color="#919191" />;
      case "read":
        return <CheckCheck {...iconProps} color="#34B7F1" />;
      case "failed":
        return <AlertCircle {...iconProps} color="#E94437" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "sent":
        return "Sent";
      case "delivered":
        return "Delivered";
      case "read":
        return "Read";
      case "failed":
        return "Failed";
      default:
        return "";
    }
  };

  // Wrapped in useCallback to prevent child widget re-renders
  const handleSendMessage = useCallback(
    async (text, file) => {
      try {
        const formData = new FormData();
        if (text && text.trim()) {
          formData.append("message", text.trim());
        }
        formData.append("profileName", selectedContact?.profileName);
        formData.append("to", selectedContact?.from);
        if (file) {
          formData.append("file", file);
        }

        const endpoint = file
          ? `${API_ENDPOINT}/message/send-file-message?cid=${selectedAccount}`
          : `${API_ENDPOINT}/message/send-text-message?cid=${selectedAccount}`;

        const response = await fetch(endpoint, {
          method: "POST",
          body: file
            ? formData
            : JSON.stringify({
                message: text.trim(),
                profileName: selectedContact?.profileName,
                to: selectedContact?.from,
              }),
          headers: {
            ...(!file && { "Content-Type": "application/json" }),
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setReloadChat((prev) => !prev);
        toast.success("Message sent");
        return result;
      } catch (error) {
        toastError(error);
        throw error;
      }
    },
    [selectedContact, selectedAccount]
  );

  // Wrapped in useCallback to stop UI blocking during typing
  const exportChat = useCallback(async () => {
    const payload = {};
    if (tagType) {
      payload.tag = tagType.trim();
    }
    if (startDate && endDate) {
      payload.startDate = startDate.format("YYYY-MM-DD");
      payload.endDate = endDate.format("YYYY-MM-DD");
    }

    try {
      setExportingChat(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}/message/export-chat?tn=${
          paramValue ? encodeURIComponent(paramValue) : ""
        }&cid=${selectedAccount}`,
        payload,
        getHeader()
      );

      const sheetData = data.map(
        ({ from, profileName, type, body, tag, updatedOn, campaignName }) => ({
          "From (Phone)": from,
          "Profile Name": profileName,
          "Message Type": type,
          "Message Body": body,
          Tag: tag,
          "Updated On (IST)": formatIST(updatedOn),
          "Campaign Name": campaignName,
        })
      );
      exportToExcel(sheetData, "chat_export.xlsx");
      toast.success("Chat exported");
    } catch (error) {
      toastError(error);
    } finally {
      setExportingChat(false);
    }
  }, [tagType, startDate, endDate, paramValue, selectedAccount]);

  useEffect(() => {
    if (chatData?.messages) {
      setMessages(chatData.messages);
      setHasMoreOlder(!!chatData.hasMore);
    }
  }, [chatData, selectedContact?.from]);

  useEffect(() => {
    if (messagesEndRef.current && !isChatLoading && chatData?.messages) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatData?.messages, selectedContact, isChatLoading]);

  const loadOlderMessages = useCallback(async () => {
    if (!selectedContact || messages.length === 0 || loadingOlder) return;
    try {
      setLoadingOlder(true);
      const oldest = messages[0]?.createdOn;
      const { data } = await axios.get(
        `/api/user-chats/${
          selectedContact.from
        }?cid=${selectedAccount}&before=${encodeURIComponent(oldest)}`,
        getHeader()
      );
      setMessages((prev) => [...(data.messages || []), ...prev]);
      setHasMoreOlder(!!data.hasMore);
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingOlder(false);
    }
  }, [selectedContact, messages, loadingOlder, selectedAccount]);

  const messagesWithDateKeys = useMemo(() => {
    let lastKey = null;
    return messages.map((message) => {
      const key = getISTDateKey(message.updatedOn);
      const showDivider = key !== lastKey;
      lastKey = key;
      return { ...message, __showDivider: showDivider };
    });
  }, [messages]);

  // Memoized Contact List to avoid re-rendering entire list when input changes
  const renderedContacts = useMemo(() => {
    return contacts.map((contact) => {
      const isSelected = selectedContact?.from === contact.from;
      const avatarColor = getAvatarColor(contact.profileName || contact.from);
      const effectiveUnreadCount = clearedUnreadFor.has(contact.from)
        ? 0
        : contact.unreadCount;
      return (
        <ListItemButton
          key={contact.from}
          selected={isSelected}
          onClick={() => handleContactClick(contact)}
          sx={{
            py: 1.25,
            px: 1.5,
            borderRadius: 2,
            mb: 0.5,
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { backgroundColor: "primary.dark" },
            },
            "&.Mui-focusVisible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: "-2px",
            },
          }}
        >
          <ListItemAvatar>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                effectiveUnreadCount ? (
                  <UnreadBadge>{effectiveUnreadCount}</UnreadBadge>
                ) : null
              }
            >
              <Avatar
                sx={{
                  bgcolor: isSelected ? "rgba(255,255,255,0.2)" : avatarColor,
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                {contact?.profileName?.[0]?.toUpperCase() || <User size={18} />}
              </Avatar>
            </Badge>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="body1" fontWeight={600} noWrap>
                  {contact.profileName || "Unknown"}
                </Typography>
                {contact.lastUpdated && (
                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      color: isSelected
                        ? "rgba(255,255,255,0.85)"
                        : "text.secondary",
                    }}
                  >
                    {getRelativeTime(contact.lastUpdated)}
                  </Typography>
                )}
              </Box>
            }
            secondary={
              <Typography
                variant="caption"
                sx={{
                  color: isSelected
                    ? "rgba(255,255,255,0.85)"
                    : "text.secondary",
                }}
                noWrap
              >
                {getMessagePreview(
                  contact.lastMessageType,
                  contact.lastMessageBody
                ) || contact.from}
              </Typography>
            }
          />
        </ListItemButton>
      );
    });
  }, [contacts, selectedContact?.from, clearedUnreadFor, handleContactClick]);

  // Memoized Messages to avoid re-rendering chat history when unneeded
  const renderedMessages = useMemo(() => {
    return messagesWithDateKeys.map((message) => {
      const isSent = isMessageSent(message);
      const formattedTime = formatIST(message.updatedOn).slice(0, -3);

      return (
        <React.Fragment key={message.id}>
          {message.__showDivider && (
            <DateDivider>
              <Chip
                label={getDateLabel(message.updatedOn)}
                size="small"
                sx={{
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  color: "text.secondary",
                }}
              />
            </DateDivider>
          )}
          <MessageBubble isSent={isSent}>
            {message.type === "template" ? (
              getMessageContent(message)
            ) : (
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-word", lineHeight: 1.5 }}
              >
                {getMessageContent(message)}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                mt: 0.5,
                gap: 0.5,
                opacity: 0.75,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                {formattedTime}
              </Typography>
              {isSent && message.status && (
                <Tooltip title={getStatusText(message.status)}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {getStatusIcon(message.status)}
                  </Box>
                </Tooltip>
              )}
            </Box>
          </MessageBubble>
        </React.Fragment>
      );
    });
  }, [messagesWithDateKeys, selectedContact?.from]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: "calc(100vh - 64px)",
        bgcolor: "background.default",
      }}
    >
      {/* --- Sidebar --- */}
      <SidebarContainer
        sx={{ display: { xs: selectedContact ? "none" : "flex", md: "flex" } }}
      >
        <Box sx={{ px: 2.5, py: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or phone"
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={17} color="#9e9e9e" />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={clearSearch}
                  >
                    <X size={15} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#F3F4F6",
                "& fieldset": { border: "1px solid transparent" },
                "&:hover fieldset": { borderColor: "divider" },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: "1px",
                },
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {isContactsLoading ? (
            <Stack spacing={1.25} sx={{ p: 2 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <Skeleton variant="circular" width={44} height={44} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="text" width="40%" height={16} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : isContactsError ? (
            <Alert
              severity="error"
              icon={<AlertCircle size={18} />}
              sx={{ m: 2, borderRadius: 2 }}
            >
              {contactsError?.message ||
                "Couldn't load your contacts. Check your connection and try again."}
            </Alert>
          ) : contacts.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ p: 4, color: "text.secondary", textAlign: "center" }}
            >
              <MessageCircle size={40} color="#c7cad1" strokeWidth={1.4} />
              <Typography variant="body2">
                {searchFrom
                  ? `No contacts match "${searchFrom}".`
                  : "No conversations yet."}
              </Typography>
            </Stack>
          ) : (
            <List sx={{ p: 1 }}>{renderedContacts}</List>
          )}
        </Box>
      </SidebarContainer>

      {/* --- Chat Area --- */}
      <ChatContainer
        sx={{ display: { xs: selectedContact ? "flex" : "none", md: "flex" } }}
      >
        {selectedContact ? (
          <>
            <ChatHeader>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <IconButton
                  onClick={handleBackToList}
                  sx={{ display: { xs: "inline-flex", md: "none" } }}
                  aria-label="Back to chat list"
                >
                  <ArrowLeft size={20} />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: getAvatarColor(
                      selectedContact?.profileName || selectedContact?.from
                    ),
                    color: "#fff",
                    width: 44,
                    height: 44,
                    fontWeight: 600,
                  }}
                >
                  {selectedContact?.profileName?.charAt(0).toUpperCase() || (
                    <User size={18} />
                  )}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    noWrap
                    sx={{ textOverflow: "ellipsis" }}
                  >
                    {selectedContact.profileName || "Unknown"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {selectedContact.from}
                  </Typography>
                </Box>
              </Box>

              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={1.25}
                alignItems={{ xs: "stretch", lg: "center" }}
                sx={{ width: { xs: "100%", lg: "auto" } }}
              >
                <Box sx={{ flex: 1 }}>
                  <DateRangePicker
                    size="small"
                    startLabel="From"
                    endLabel="To"
                    onChange={(newRange) => {
                      setStartDate(newRange.startDate);
                      setEndDate(newRange.endDate);
                    }}
                    format="DD/MM/YYYY"
                    showQuickSelect={true}
                  />
                </Box>
                <TextField
                  size="small"
                  value={tagType}
                  onChange={(e) => setTagType(e.target.value)}
                  variant="outlined"
                  placeholder="Filter by tag"
                  sx={{
                    minWidth: 130,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                />
                <LoadingButton
                  loading={exportingChat}
                  onClick={exportChat}
                  startIcon={<Download size={17} />}
                  variant="contained"
                  disableElevation
                  size="medium"
                  sx={{ whiteSpace: "nowrap", borderRadius: "8px" }}
                >
                  Export
                </LoadingButton>
                <Tooltip
                  title={
                    showProfilePanel ? "Hide contact info" : "Show contact info"
                  }
                >
                  <IconButton
                    onClick={() => setShowProfilePanel((prev) => !prev)}
                    sx={{
                      display: { xs: "none", md: "inline-flex" },
                      color: showProfilePanel
                        ? "primary.main"
                        : "text.secondary",
                    }}
                    aria-label="Toggle contact info panel"
                  >
                    {showProfilePanel ? (
                      <PanelRightClose size={20} />
                    ) : (
                      <PanelRightOpen size={20} />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
            </ChatHeader>

            <ChatMessages>
              {isChatLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={30} thickness={4} />
                </Box>
              )}
              {isChatError && (
                <Alert
                  severity="error"
                  icon={<AlertCircle size={18} />}
                  sx={{ m: 2, borderRadius: 2 }}
                  action={
                    <IconButton
                      size="small"
                      onClick={() => setReloadChat((prev) => !prev)}
                    >
                      <RefreshCw size={16} />
                    </IconButton>
                  }
                >
                  {chatError?.message ||
                    "Couldn't load this conversation. Try again."}
                </Alert>
              )}

              {!isChatLoading && hasMoreOlder && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                  <LoadingButton
                    size="small"
                    loading={loadingOlder}
                    onClick={loadOlderMessages}
                    variant="outlined"
                    sx={{ borderRadius: "999px", textTransform: "none" }}
                  >
                    Load earlier messages
                  </LoadingButton>
                </Box>
              )}

              {renderedMessages}

              {!isChatLoading && !isChatError && messages.length === 0 && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1.5,
                    color: "text.secondary",
                  }}
                >
                  <MessageCircle size={56} color="#c7cad1" strokeWidth={1.2} />
                  <Typography variant="body1" fontWeight={500}>
                    No messages yet
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ maxWidth: 300, textAlign: "center" }}
                  >
                    Send a message below to start the conversation with{" "}
                    {selectedContact.profileName || "this contact"}.
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </ChatMessages>

            <InputContainer>
              <SendMessage onSend={handleSendMessage} />
            </InputContainer>
          </>
        ) : (
          <Fade in>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
                color: "text.secondary",
                p: 3,
              }}
            >
              <MessageCircle size={72} color="#dbe0e6" strokeWidth={1.1} />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Select a contact
              </Typography>
              <Typography
                variant="body2"
                sx={{ maxWidth: 340, textAlign: "center" }}
              >
                Choose a contact from the list to view your conversation, or
                search to find someone.
              </Typography>
            </Box>
          </Fade>
        )}
      </ChatContainer>

      {selectedContact && showProfilePanel && (
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <LookupProfile selectedContact={selectedContact} />
        </Box>
      )}
    </Box>
  );
};

export default Chats;
